import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getOrgEvents, getEventFeedbacks, rsvpEvent, deleteEvent, updateEvent } from '../../api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';
import Alert from '../../components/Alert';
import { StarRating } from '../../components/StarRating';
import Loading from '../../components/Loading';
import EmptyState from '../../components/EmptyState';
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  ServerIcon,
  CheckCircleIcon,
  XCircleIcon,
  TicketIcon,
  ChatBubbleLeftEllipsisIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

// Helper to get local date string YYYY-MM-DD
const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

function tzOffsetMinutes(date, tz) {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
    const p = {};
    for (const { type, value } of fmt.formatToParts(date)) p[type] = value;
    const wall = Date.UTC(+p.year, +p.month - 1, +p.day, +(p.hour === '24' ? '0' : p.hour), +p.minute, +p.second);
    return (wall - date.getTime()) / 60000;
  } catch { return 0; }
}

function tzAbbr(date, tz) {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(date).find(p => p.type === 'timeZoneName')?.value || tz;
  } catch { return tz; }
}

const convertEventTime = (dateStr, timeStr, creatorTz) => {
  if (!dateStr || !timeStr) return { date: dateStr, time: timeStr, originalTime: timeStr, tzLabel: '', sameTz: true };
  const fromTz = creatorTz || 'UTC';
  try {
    const [y, mo, d] = dateStr.split('-').map(Number);
    const [h, mi] = timeStr.split(':').map(Number);
    const guessUtc = new Date(Date.UTC(y, mo - 1, d, h, mi, 0));
    let offset = tzOffsetMinutes(guessUtc, fromTz);
    let actualUtc = new Date(guessUtc.getTime() - offset * 60000);
    const offset2 = tzOffsetMinutes(actualUtc, fromTz);
    if (offset2 !== offset) actualUtc = new Date(guessUtc.getTime() - offset2 * 60000);
    const vOff = tzOffsetMinutes(actualUtc, USER_TIMEZONE);
    const viewerMs = actualUtc.getTime() + vOff * 60000;
    const vd = new Date(viewerMs);
    const sameTz = fromTz === USER_TIMEZONE;
    const creatorAbbr = tzAbbr(actualUtc, fromTz);
    const viewerAbbr = tzAbbr(actualUtc, USER_TIMEZONE);
    return {
      date: `${vd.getUTCFullYear()}-${String(vd.getUTCMonth()+1).padStart(2,'0')}-${String(vd.getUTCDate()).padStart(2,'0')}`,
      time: `${String(vd.getUTCHours()).padStart(2,'0')}:${String(vd.getUTCMinutes()).padStart(2,'0')}`,
      originalTime: timeStr,
      originalTzAbbr: creatorAbbr,
      viewerTzAbbr: viewerAbbr,
      tzLabel: sameTz ? creatorAbbr : `${viewerAbbr} (${timeStr} ${creatorAbbr})`,
      sameTz
    };
  } catch {
    return { date: dateStr, time: timeStr, originalTime: timeStr, tzLabel: '', sameTz: true };
  }
};

export default function EventsPage() {
  const { orgId } = useParams();
  const { user, organizations } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEvent, setShowEvent] = useState(null);
  const [eventFeedbacks, setEventFeedbacks] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [error, setError] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', description: '', date: '', time: '', location: '', server: '' });
  const [saving, setSaving] = useState(false);

  const org = organizations.find(o => o._id === orgId);
  const isOwner = org?.ownerId === user?._id || user?.role === 'admin';

  useEffect(() => { loadEvents(); }, [orgId]);

  const loadEvents = async () => {
    try {
      const res = await getOrgEvents(orgId);
      setEvents(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openEventDetail = async (event) => {
    setShowEvent(event);
    try {
      const res = await getEventFeedbacks(event._id);
      setEventFeedbacks(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRSVP = async (eventId, status) => {
    try {
      const res = await rsvpEvent(eventId, status);
      setEvents(prev => prev.map(e => e._id === eventId ? res.data : e));
      if (showEvent?._id === eventId) setShowEvent(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await deleteEvent(eventId);
      setEvents(prev => prev.filter(e => e._id !== eventId));
      setShowEvent(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete event');
    }
  };

  const openEdit = (event) => {
    setEditForm({
      title: event.title || '',
      description: event.description || '',
      date: event.date || '',
      time: event.time || '',
      location: event.location || '',
      server: event.server || ''
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!showEvent) return;
    setSaving(true);
    try {
      const res = await updateEvent(showEvent._id, { ...editForm, timezone: showEvent.timezone });
      setEvents(prev => prev.map(ev => ev._id === showEvent._id ? res.data : ev));
      setShowEvent(res.data);
      setShowEdit(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const todayStr = toLocalDateStr(new Date());
  const upcomingEvents = events.filter(e => e.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date));
  const pastEvents = events.filter(e => e.date < todayStr).sort((a, b) => b.date.localeCompare(a.date));

  const displayedEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;

  const getUserRSVP = (event) => {
    if (event.attending?.some(a => a.userId === user._id)) return 'attending';
    if (event.notAttending?.some(a => a.userId === user._id)) return 'not-attending';
    return null;
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Events</h2>
          <div className="subtitle">{org?.name || 'Organization'}</div>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'upcoming' ? 'active' : ''}`} onClick={() => setActiveTab('upcoming')}>
          Upcoming ({upcomingEvents.length})
        </button>
        <button className={`tab ${activeTab === 'past' ? 'active' : ''}`} onClick={() => setActiveTab('past')}>
          Past ({pastEvents.length})
        </button>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {displayedEvents.length === 0 ? (
        <EmptyState
          icon={<TicketIcon style={{ width: 48, height: 48 }} />}
          title={activeTab === 'upcoming' ? 'No Upcoming Events' : 'No Past Events'}
          description={activeTab === 'upcoming' ? 'No events scheduled yet. Check back later!' : 'No events have taken place yet.'}
        />
      ) : (
        <div className="events-list">
          {displayedEvents.map(event => {
            const ct = convertEventTime(event.date, event.time, event.timezone);
            const eventDate = new Date(ct.date + 'T12:00:00');
            const isPast = event.date < todayStr;
            return (
              <div key={event._id} className="event-row" onClick={() => openEventDetail(event)}>
                <div className="event-row-date">
                  <div className="date-day">{eventDate.getDate()}</div>
                  <div className="date-month">{eventDate.toLocaleDateString('en-US', { month: 'short' })}</div>
                </div>
                <div className="event-row-info">
                  <div className="event-row-title">
                    {event.title}
                    {isPast && <span className="badge badge-past" style={{ marginLeft: 8 }}>Past</span>}
                  </div>
                  <div className="event-row-meta">
                    {event.time && <span><ClockIcon className="icon" /> {ct.time} <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{ct.sameTz ? ct.viewerTzAbbr : ct.tzLabel}</span></span>}
                    {event.location && <span><MapPinIcon className="icon" /> {event.location}</span>}
                    {event.server && <span><ServerIcon className="icon" /> {event.server}</span>}
                  </div>
                </div>
                <div className="event-row-stats">
                  <span className="badge badge-success"><CheckCircleIcon className="icon" /> {event.attending?.length || 0}</span>
                  <span className="badge badge-danger"><XCircleIcon className="icon" /> {event.notAttending?.length || 0}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Event Detail Modal */}
      <Modal isOpen={!!showEvent} onClose={() => { setShowEvent(null); setEventFeedbacks([]); }} title={showEvent?.title || ''} large
        footer={isOwner && showEvent ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(showEvent)}>
              <PencilIcon className="icon" /> Edit
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteEvent(showEvent._id)}>
              <TrashIcon className="icon" /> Delete Event
            </button>
          </div>
        ) : null}
      >
        {showEvent && (
          <>
            {showEvent.description && (
              <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 14, lineHeight: 1.6 }}>
                {showEvent.description}
              </p>
            )}

            <div className="event-detail-grid">
              <div className="event-detail-item">
                <span className="detail-icon"><CalendarIcon className="icon" /></span>
                <div>
                  <div className="detail-label">Date</div>
                  {new Date(showEvent.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              {showEvent.time && (() => {
                const ct = convertEventTime(showEvent.date, showEvent.time, showEvent.timezone);
                return (
                <div className="event-detail-item">
                  <span className="detail-icon"><ClockIcon className="icon" /></span>
                  <div>
                    <div className="detail-label">Time</div>
                    {ct.time}
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                      {ct.sameTz ? ct.viewerTzAbbr : ct.tzLabel}
                    </span>
                  </div>
                </div>
                );
              })()}
              {showEvent.location && (
                <div className="event-detail-item">
                  <span className="detail-icon"><MapPinIcon className="icon" /></span>
                  <div><div className="detail-label">Location</div>{showEvent.location}</div>
                </div>
              )}
              {showEvent.server && (
                <div className="event-detail-item">
                  <span className="detail-icon"><ServerIcon className="icon" /></span>
                  <div><div className="detail-label">Server</div>{showEvent.server}</div>
                </div>
              )}
            </div>

            <div className="rsvp-section">
              <h4>Attendance</h4>
              <div className="rsvp-buttons">
                <button
                  className={`btn btn-sm ${getUserRSVP(showEvent) === 'attending' ? 'btn-success' : 'btn-secondary'}`}
                  onClick={() => handleRSVP(showEvent._id, 'attending')}
                >
                  <CheckCircleIcon className="icon" /> I'll be there
                </button>
                <button
                  className={`btn btn-sm ${getUserRSVP(showEvent) === 'not-attending' ? 'btn-danger' : 'btn-secondary'}`}
                  onClick={() => handleRSVP(showEvent._id, 'not-attending')}
                >
                  <XCircleIcon className="icon" /> Can't make it
                </button>
              </div>
              <div className="rsvp-count">
                <span style={{ color: 'var(--success)' }}><CheckCircleIcon className="icon" /> {showEvent.attending?.length || 0} attending</span>
                <span style={{ color: 'var(--danger)' }}><XCircleIcon className="icon" /> {showEvent.notAttending?.length || 0} not attending</span>
              </div>
              {showEvent.attending?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {showEvent.attending.map((a, i) => <span key={i} className="badge badge-success">{a.displayName}</span>)}
                  </div>
                </div>
              )}
            </div>

            {/* Feedbacks section */}
            {eventFeedbacks.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ChatBubbleLeftEllipsisIcon className="icon" /> Feedbacks ({eventFeedbacks.length})
                </h4>
                <div className="feedback-list">
                  {eventFeedbacks.map(fb => (
                    <div key={fb._id} className="feedback-item">
                      <div className="feedback-header">
                        <span className="feedback-author">{fb.displayName}</span>
                        <span className="feedback-date">{new Date(fb.createdAt).toLocaleDateString()}</span>
                      </div>
                      <StarRating rating={fb.rating} />
                      <div className="feedback-content">{fb.content}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Edit Event Modal */}
      <Modal isOpen={showEdit} onClose={() => setShowEdit(false)} title="Edit Event"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowEdit(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </>
        }
      >
        <form onSubmit={handleSaveEdit}>
          <div className="form-group">
            <label>Title</label>
            <input className="form-input" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-input" value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Date</label>
              <input className="form-input" type="date" value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>Time</label>
              <input className="form-input" type="time" value={editForm.time} onChange={e => setEditForm(f => ({ ...f, time: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label>Location</label>
            <input className="form-input" value={editForm.location} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Moorland Stables" />
          </div>
          <div className="form-group">
            <label>Server</label>
            <input className="form-input" value={editForm.server} onChange={e => setEditForm(f => ({ ...f, server: e.target.value }))} placeholder="e.g. Star Glow" />
          </div>
        </form>
      </Modal>
    </div>
  );
}
